import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UserCheck, Users as UsersIcon, Filter, ArrowRightLeft, X as XIcon, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_OPTIONS = [
  { id: "new",       label: "New",       color: "#e1b7c1" },
  { id: "contacted", label: "Contacted", color: "#bf858e" },
  { id: "converted", label: "Converted", color: "#050303" },
  { id: "lost",      label: "Lost",      color: "#d1cece" },
];
const STATUS_META = Object.fromEntries(STATUS_OPTIONS.map(s => [s.id, s]));

// Red for SLA-breach — the ONLY warning-red used in the app. Not part of the
// core brand palette but purpose-built to grab attention on stale leads.
const STALE_RED = "#c0362f";

const INTENT_LABEL = { buy: "Buy", sell: "Sell", both: "Buy + Sell" };


function isStale(lead, slaHours = 24) {
  if ((lead.status || "new") !== "new") return false;
  if (!lead.created_at) return false;
  const created = new Date(lead.created_at).getTime();
  if (Number.isNaN(created)) return false;
  return (Date.now() - created) / 3600000 >= slaHours;
}


function fmtAge(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const hrs = Math.floor(ms / 3600000);
  if (hrs < 1) return `${Math.max(1, Math.floor(ms / 60000))}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}


export default function ClientLeads() {
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAgent, setFilterAgent] = useState("");
  const [filterStale, setFilterStale] = useState(false);
  const [reassigning, setReassigning] = useState(null); // lead object

  const loadAll = async () => {
    const [s, l, a] = await Promise.all([
      api.get("/admin/client-leads/summary"),
      api.get("/admin/client-leads", { params: {
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(filterAgent ? { agent_id: filterAgent } : {}),
        ...(filterStale ? { stale: true } : {}),
      }}),
      api.get("/auth/agents"),
    ]);
    setSummary(s.data);
    setLeads(l.data || []);
    setAgents(a.data || []);
  };

  useEffect(() => { loadAll(); }, [filterStatus, filterAgent, filterStale]); // eslint-disable-line

  const setStatus = async (lead, next) => {
    // Optimistic
    setLeads(rows => rows.map(r => r.id === lead.id ? { ...r, status: next } : r));
    try {
      await api.patch(`/admin/client-leads/${lead.id}/status`, { status: next });
      toast.success(`Marked as ${next}`);
      // Refresh only the summary counts (leads list already updated optimistically).
      const s = await api.get("/admin/client-leads/summary");
      setSummary(s.data);
    } catch {
      toast.error("Status update failed");
      loadAll();
    }
  };

  const targetableAgents = useMemo(
    () => agents.filter(a => a.role === "agent" || a.role === "admin"),
    [agents]
  );

  if (!summary) {
    return <div className="p-12 text-sm text-[#050303]/60">Loading client leads…</div>;
  }

  const total = summary.total_leads || 0;
  const bs = summary.by_status || {};
  const bi = summary.by_intent || {};

  return (
    <div className="space-y-7" data-testid="client-leads-page">
      <header>
        <div className="overline">Admin · Broker Only</div>
        <h1 className="font-heading text-5xl mt-2 flex items-center gap-3">
          <UserCheck size={36} strokeWidth={1.5} className="text-[#bf858e]" />
          Client Leads
        </h1>
        <p className="text-sm text-[#050303]/60 mt-2">
          Every submission from the public <a href="/clients" target="_blank" rel="noopener noreferrer" className="text-[#bf858e] hover:text-[#050303] underline underline-offset-2">/clients</a> page —
          filter, move through the funnel, or reassign to a different on-duty agent.
        </p>
      </header>

      {/* Funnel stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <StatCard label="Total Leads" value={total} testId="stat-total"
                  active={!filterStatus && !filterStale} onClick={() => { setFilterStatus(""); setFilterStale(false); }} />
        {STATUS_OPTIONS.map(s => (
          <StatCard
            key={s.id}
            label={s.label}
            value={bs[s.id] || 0}
            color={s.color}
            testId={`stat-${s.id}`}
            active={filterStatus === s.id && !filterStale}
            onClick={() => { setFilterStale(false); setFilterStatus(filterStatus === s.id ? "" : s.id); }}
          />
        ))}
        <StatCard
          label={`Stale > ${summary.sla_hours || 24}h`}
          value={summary.stale_count || 0}
          color={STALE_RED}
          testId="stat-stale"
          active={filterStale}
          onClick={() => { setFilterStale(!filterStale); setFilterStatus(""); }}
          alert={(summary.stale_count || 0) > 0}
        />
      </div>

      {/* Intent breakdown */}
      <div className="core-card p-5">
        <div className="overline mb-2">Intent</div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span data-testid="intent-buy"><strong className="text-[#bf858e]">{bi.buy || 0}</strong> buyers</span>
          <span className="text-[#050303]/30">·</span>
          <span data-testid="intent-sell"><strong className="text-[#bf858e]">{bi.sell || 0}</strong> sellers</span>
          <span className="text-[#050303]/30">·</span>
          <span data-testid="intent-both"><strong className="text-[#bf858e]">{bi.both || 0}</strong> buying + selling</span>
        </div>
      </div>

      {/* Per-agent breakdown */}
      <div className="core-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <UsersIcon size={16} className="text-[#bf858e]" />
            <div className="overline">Per-Agent Assignments</div>
          </div>
          {filterAgent && (
            <button
              onClick={() => setFilterAgent("")}
              className="text-xs text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
              data-testid="clear-agent-filter"
            ><XIcon size={12} /> Clear agent filter</button>
          )}
        </div>
        {summary.per_agent.length === 0 ? (
          <div className="text-xs text-[#050303]/40 py-3">No agents have leads yet. Toggle agents On-Duty in Admin → Agents.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#050303]/55 border-b border-[#d1cece]">
                  <th className="text-left py-2">Agent</th>
                  <th className="text-right py-2">On Duty</th>
                  <th className="text-right py-2">Total</th>
                  <th className="text-right py-2">New</th>
                  <th className="text-right py-2">Contacted</th>
                  <th className="text-right py-2">Converted</th>
                  <th className="text-right py-2">Lost</th>
                  <th className="text-right py-2" style={{ color: STALE_RED }}>Stale</th>
                </tr>
              </thead>
              <tbody>
                {summary.per_agent.map(row => (
                  <tr
                    key={row.agent_id}
                    className={`border-b border-[#d1cece]/50 hover:bg-[#e1b7c1]/10 cursor-pointer ${filterAgent === row.agent_id ? "bg-[#e1b7c1]/20" : ""}`}
                    onClick={() => setFilterAgent(filterAgent === row.agent_id ? "" : row.agent_id)}
                    data-testid={`per-agent-row-${row.agent_id}`}
                  >
                    <td className="py-2">
                      <div className="font-semibold">{row.agent_name}</div>
                      <div className="text-[10px] text-[#050303]/50">{row.agent_email} · <span className="capitalize">{row.role}</span></div>
                    </td>
                    <td className="text-right py-2">
                      {row.on_duty ? <span className="text-[10px] text-[#bf858e] font-bold uppercase tracking-widest">On Duty</span> : <span className="text-[10px] text-[#050303]/40">—</span>}
                    </td>
                    <td className="text-right py-2 font-heading">{row.total}</td>
                    <td className="text-right py-2">{row.new}</td>
                    <td className="text-right py-2">{row.contacted}</td>
                    <td className="text-right py-2">{row.converted}</td>
                    <td className="text-right py-2 text-[#050303]/50">{row.lost}</td>
                    <td className="text-right py-2 font-bold" style={{ color: (row.stale || 0) > 0 ? STALE_RED : "rgba(5,3,3,0.3)" }}>
                      {row.stale || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead table */}
      <div className="core-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="overline flex items-center gap-2">
            <Filter size={12} /> All Leads {(filterStatus || filterAgent) && <span className="text-[#bf858e] normal-case tracking-normal font-normal">(filtered)</span>}
          </div>
          <div className="text-xs text-[#050303]/55" data-testid="leads-count">{leads.length} shown</div>
        </div>
        {leads.length === 0 ? (
          <div className="text-sm text-[#050303]/50 py-8 text-center" data-testid="no-leads">
            No leads match the current filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#050303]/55 border-b border-[#d1cece]">
                  <th className="text-left py-2">Lead</th>
                  <th className="text-left py-2">Intent</th>
                  <th className="text-left py-2">Timeline / Area</th>
                  <th className="text-left py-2">Assigned</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => {
                  const stale = isStale(lead, summary.sla_hours || 24);
                  return (
                  <tr
                    key={lead.id}
                    className={`border-b border-[#d1cece]/50 align-top ${stale ? "bg-[rgba(192,54,47,0.04)]" : ""}`}
                    style={stale ? { borderLeft: `3px solid ${STALE_RED}` } : {}}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="py-3 pr-3 min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold">{lead.name}</div>
                        {stale && (
                          <span
                            className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full"
                            style={{ background: STALE_RED, color: "#fff" }}
                            title={`Sat in "New" for ${fmtAge(lead.created_at)} — SLA is ${summary.sla_hours || 24}h`}
                            data-testid={`stale-badge-${lead.id}`}
                          >
                            {summary.sla_hours || 24}h+
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#050303]/55"><a href={`mailto:${lead.email}`} className="hover:text-[#bf858e]">{lead.email}</a></div>
                      <div className="text-[11px] text-[#050303]/55">{lead.phone || "—"}</div>
                      <div
                        className="text-[10px] mt-1"
                        style={{ color: stale ? STALE_RED : "rgba(5,3,3,0.4)" }}
                        data-testid={`lead-age-${lead.id}`}
                      >
                        {fmtAge(lead.created_at)} · {new Date(lead.created_at).toLocaleString()}
                      </div>
                    </td>
                    <td className="py-3 pr-3">
                      <span className="text-[10px] uppercase tracking-widest bg-[#e1b7c1]/40 text-[#050303] px-2 py-0.5 rounded-full">
                        {INTENT_LABEL[lead.intent] || lead.intent}
                      </span>
                    </td>
                    <td className="py-3 pr-3 min-w-[140px]">
                      <div className="text-[12px] text-[#050303]">{lead.timeline || "—"}</div>
                      <div className="text-[11px] text-[#050303]/55">{lead.neighborhood || "—"}</div>
                    </td>
                    <td className="py-3 pr-3 min-w-[160px]">
                      <div className="text-[12px] font-semibold">{lead.assigned_agent_name || "—"}</div>
                      <div className="text-[10px] text-[#050303]/50">
                        {lead.assigned_role === "broker" && <span className="text-[#bf858e]">broker fallback</span>}
                        {lead.reassigned_at && <span className="ml-1 text-[#bf858e]">· reassigned</span>}
                      </div>
                    </td>
                    <td className="py-3 pr-3">
                      <StatusSelect lead={lead} onChange={(next) => setStatus(lead, next)} />
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => setReassigning(lead)}
                        className="text-[11px] text-[#050303]/70 hover:text-[#bf858e] flex items-center gap-1 ml-auto"
                        data-testid={`reassign-btn-${lead.id}`}
                        title="Hand off to another agent"
                      >
                        <ArrowRightLeft size={12} /> Reassign
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reassigning && (
        <ReassignDialog
          lead={reassigning}
          agents={targetableAgents}
          onClose={() => setReassigning(null)}
          onDone={() => { setReassigning(null); loadAll(); }}
        />
      )}
    </div>
  );
}


function StatCard({ label, value, color, testId, active, onClick, alert }) {
  return (
    <button
      onClick={onClick}
      className={`core-card-flat p-4 text-left transition ${active ? "ring-2" : "hover:-translate-y-0.5"} ${alert ? "animate-[pulse_2.5s_ease-in-out_infinite]" : ""}`}
      style={{
        ...(color ? { borderLeft: `3px solid ${color}` } : {}),
        ...(active ? { "--tw-ring-color": color || "#bf858e" } : {}),
        ...(alert && value > 0 ? { background: "rgba(192,54,47,0.06)" } : {}),
      }}
      data-testid={testId}
    >
      <div className="overline text-[10px]" style={alert && value > 0 ? { color: color || "#050303" } : {}}>{label}</div>
      <div
        className="font-heading text-3xl mt-1"
        style={alert && value > 0 ? { color: color || "#050303" } : {}}
      >
        {value}
      </div>
    </button>
  );
}


function StatusSelect({ lead, onChange }) {
  const current = STATUS_META[lead.status] || STATUS_META.new;
  return (
    <div className="relative inline-block">
      <select
        value={lead.status || "new"}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none pr-6 pl-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-semibold border cursor-pointer focus:outline-none"
        style={{ borderColor: current.color, color: current.color, background: `${current.color}18` }}
        data-testid={`status-select-${lead.id}`}
      >
        {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: current.color }} />
    </div>
  );
}


function ReassignDialog({ lead, agents, onClose, onDone }) {
  const [agentId, setAgentId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agentId) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/client-leads/${lead.id}/reassign`, {
        agent_id: agentId, reason,
      });
      if (data.no_change) toast.info("Already assigned to that agent");
      else toast.success(`Reassigned to ${data.new_agent_name}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassign failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="reassign-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Reassign Lead</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-[#050303]/70 -mt-2 mb-3">
          Move <strong>{lead.name}</strong> from{" "}
          <strong>{lead.assigned_agent_name || "—"}</strong> to a different agent.
          Both the new and previous agent will be emailed.
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline text-[10px] block mb-1">New agent</label>
            <select
              required value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm"
              data-testid="reassign-agent-select"
            >
              <option value="">Select an agent…</option>
              {agents
                .filter(a => a.id !== lead.assigned_agent_id)
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.email} {a.on_duty ? "· ON DUTY" : ""}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="overline text-[10px] block mb-1">Reason (optional)</label>
            <textarea
              rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Agent out of office · Client requested a different rep"
              className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm"
              data-testid="reassign-reason"
            />
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button" onClick={onClose}
              className="border border-[#050303] text-[#050303] pill-btn hover:bg-[#050303] hover:text-white"
              data-testid="reassign-cancel"
            >Cancel</button>
            <button
              type="submit" disabled={saving || !agentId}
              className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex-1 disabled:opacity-60"
              data-testid="reassign-submit"
            >{saving ? "Reassigning…" : "Reassign lead"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
